/**
 * The small set of primitives every screen is built from.
 *
 * Deliberately few: a chip, a stat, a panel, an empty state, a table. An analyst should not have
 * to learn a visual language, so there are four tones and one accent colour, and nothing here
 * animates or decorates.
 */

import type { ReactNode } from "react";
import type { Tone } from "../lib/model";

const TONE_CLASS: Record<Tone, string> = {
  good: "text-good bg-good-bg border-good-line",
  warn: "text-warn bg-warn-bg border-warn-line",
  bad: "text-bad bg-bad-bg border-bad-line",
  neutral: "text-neutral bg-neutral-bg border-neutral-line",
};

const TONE_TEXT: Record<Tone, string> = {
  good: "text-good",
  warn: "text-warn",
  bad: "text-bad",
  neutral: "text-neutral",
};

const TONE_BORDER: Record<Tone, string> = {
  good: "border-good",
  warn: "border-warn",
  bad: "border-bad",
  neutral: "border-neutral",
};

export function Chip({ text, tone = "neutral" }: { text: string; tone?: Tone }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${TONE_CLASS[tone]}`}
    >
      {text}
    </span>
  );
}

/** A coloured dot — enough to scan a column of forty rows before reading any of it. */
export function Dot({ tone }: { tone: Tone }) {
  const fill: Record<Tone, string> = {
    good: "bg-good",
    warn: "bg-warn",
    bad: "bg-bad",
    neutral: "bg-neutral-line",
  };
  return <span className={`inline-block size-2 shrink-0 rounded-full ${fill[tone]}`} />;
}

export function PageHeader({
  title,
  subtitle,
  chips,
  actions,
  breadcrumb,
}: {
  title: string;
  subtitle?: ReactNode;
  chips?: ReactNode;
  actions?: ReactNode;
  /** Where this page sits. Worth showing when it is not one of the rail's own sections. */
  breadcrumb?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="min-w-0">
        {breadcrumb ?
          <div className="mb-1.5 text-sm text-muted">{breadcrumb}</div>
        : null}
        <h1 className="truncate text-2xl leading-tight font-semibold tracking-tight">{title}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
          {subtitle}
          {chips}
        </div>
      </div>
      {actions ?
        <div className="flex shrink-0 flex-col gap-2">{actions}</div>
      : null}
    </div>
  );
}

/** A row of figures. Flatter than a card grid, which reserves space it does not need. */
export function Stats({ items }: { items: [string, ReactNode][] }) {
  return (
    <div className="flex flex-wrap gap-x-9 gap-y-3">
      {items.map(([label, value]) => (
        <div key={label}>
          <div className="text-2xl leading-tight font-semibold">{value}</div>
          <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}

export function VerdictPanel({
  label,
  meaning,
  tone,
}: {
  label: string;
  meaning: string;
  tone: Tone;
}) {
  return (
    <div className={`rounded-xl border border-line border-l-4 bg-white p-5 ${TONE_BORDER[tone]}`}>
      <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
        Recomendación sugerida
      </div>
      <div className={`mt-0.5 text-2xl leading-tight font-bold ${TONE_TEXT[tone]}`}>{label}</div>
      <div className="mt-1 text-sm text-muted">{meaning}</div>
    </div>
  );
}

export function EmptyState({
  title,
  text,
  action,
}: {
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-line bg-canvas px-6 py-12 text-center">
      <div className="font-semibold">{title}</div>
      {text ?
        <div className="mx-auto mt-1 max-w-xl text-sm text-muted">{text}</div>
      : null}
      {action ?
        <div className="mt-4">{action}</div>
      : null}
    </div>
  );
}

export function Button({
  children,
  variant = "secondary",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost";
}) {
  const styles = {
    primary:
      "bg-primary text-white hover:bg-primary-hover disabled:bg-neutral-line disabled:text-muted",
    secondary: "border border-line bg-white hover:bg-canvas disabled:text-muted",
    ghost: "hover:bg-canvas",
  }[variant];
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed ${styles} ${props.className ?? ""}`}
    >
      {children}
    </button>
  );
}

export function SectionTitle({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mt-8 mb-3">
      <h2 className="text-base font-semibold">{children}</h2>
      {hint ?
        <p className="mt-0.5 text-sm text-muted">{hint}</p>
      : null}
    </div>
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return (
    <th
      className={`border-b border-line bg-canvas px-3 py-2 text-xs font-semibold tracking-wide text-muted uppercase ${right ? "text-right" : "text-left"}`}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  right,
  className = "",
}: {
  children: ReactNode;
  right?: boolean;
  className?: string;
}) {
  return (
    <td
      className={`border-b border-line px-3 py-2 align-top ${right ? "text-right tabular-nums" : ""} ${className}`}
    >
      {children}
    </td>
  );
}

/** An error an analyst can act on, with the original text one click away. */
export function ErrorBanner({ message, detail }: { message: string; detail?: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-bad-line bg-bad-bg px-4 py-3 text-sm text-bad"
    >
      <div>{message}</div>
      {detail ?
        <details className="mt-2">
          <summary className="cursor-pointer text-xs opacity-80">Detalle técnico</summary>
          <pre className="mt-1 overflow-x-auto text-xs whitespace-pre-wrap opacity-80">
            {detail}
          </pre>
        </details>
      : null}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 py-8 text-sm text-muted"
    >
      <span className="size-4 animate-spin rounded-full border-2 border-line border-t-primary" />
      {label ?? "Cargando…"}
    </div>
  );
}
